import React, { useRef } from "react";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { UseFormSetError, UseFormClearErrors } from "react-hook-form";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
interface AutocompleteLocationsProps {
  FieldName?: string;
  placeholder?: string;
  register: any;
  setValue: any;
  setError: UseFormSetError<any>;
  clearErrors: UseFormClearErrors<any>;
  color?: string;
  label: string;
  required?: boolean;
  className?: string;
  error?: string;
  stateFieldName?: string;
  postCodeFieldName?: string;
  disabled?: boolean;
}

const stateMapping: Record<string, string> = {
  "New South Wales": "NSW",
  "Northern Territory": "NT",
  Queensland: "QLD",
  Victoria: "VIC",
  "South Australia": "SA",
  Tasmania: "TAS",
  "Western Australia": "WA",
  "Australian Capital Territory": "ACT",
};

const MapKey = process.env.REACT_APP_GOOGLE_PLACE_KEY as string;

const AutocompleteLocations: React.FC<AutocompleteLocationsProps> = ({
  FieldName = "Location",
  placeholder = "",
  register,
  setValue,
  setError,
  clearErrors,
  label,
  required = false,
  className = "",
  error,
  stateFieldName = "",
  postCodeFieldName = "",
  disabled = false,
  ...props
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: MapKey,
    libraries: ["places"],
  });

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const handleLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current?.getPlace();

    if (!place?.geometry) {
      clearErrors(FieldName);
      return;
    }

    clearErrors(FieldName);

    if (place.geometry && place.geometry.location) {
      // const lat = place.geometry.location.lat();
      // const lng = place.geometry.location.lng();

      let state = "";

      let city = "";
      let zipCode = "";
      let district = "";
      if (!place?.address_components) return;
      for (const component of place.address_components ?? []) {
        for (const type of component.types) {
          if (type === "locality") {
            city = component.long_name;
          } else if (type === "administrative_area_level_1") {
            state = component.long_name;
          } else if (type === "postal_code") {
            zipCode = component.long_name;
          } else if (type === "administrative_area_level_2") {
            district = component.long_name;
          }
        }
      }
      const shortState = stateMapping[state];
      if (shortState && stateFieldName) {
        setValue(stateFieldName, shortState);
        clearErrors(stateFieldName);
      } else {
        setValue(stateFieldName, "");
      }
      if (postCodeFieldName && zipCode) {
        setValue(postCodeFieldName, zipCode);
        clearErrors(postCodeFieldName);
      } else {
        setValue(postCodeFieldName, "");
      }
      const formattedAddressWithState = `${place.formatted_address}`;
      setValue(FieldName, formattedAddressWithState);
      // Clear errors after selecting a valid address
      clearErrors(FieldName);
    }
  };

  return (
    <div className={`w-full ${className} relative`}>
      <label
        htmlFor={FieldName}
        className="block font-Mulish text-base font-normal text-lightBlue mb-1 text-left"
      >
        {label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()}
        {required && <span className="text-red-500">*</span>}
      </label>
      {isLoaded ? (
        <Autocomplete
          onLoad={handleLoad}
          onPlaceChanged={handlePlaceSelect}
          options={{
            componentRestrictions: { country: "AU" },
          }}
          className="relative"
        >
          <>
            <input
              type="text"
              placeholder={placeholder}
              {...register(`${FieldName}`, {
                required: required ? `${label} is required` : false,
              })}
              className={`w-full px-4 py-4 border-2 font-Mulish rounded-[10px] focus:outline-none placeholder-gray-400  ${
                error ? "border-red-500 ring-red-500" : "border-[#EAECF0]"
              }`}
              disabled={disabled}
            />
            {error && (
              <ErrorsMessage
                title={
                  error
                    ?.replace(/[^\w\s]/gi, "")
                    .toLowerCase()
                    .replace(/^\w/, (c) => c.toUpperCase()) ?? ""
                }
                className="text-right absolute top-[100%] right-0"
              />
            )}
          </>
        </Autocomplete>
      ) : (
        <input
          type="text"
          placeholder={placeholder}
          disabled
          className={`w-full px-4 py-4 border-2 font-Mulish rounded-[10px] focus:outline-none placeholder-gray-400`}
        />
      )}
    </div>
  );
};

export default AutocompleteLocations;
